'use client';

import { useEffect, useState } from 'react';
import {
  decodeReactError,
  reportClientError,
} from '@/lib/report-client-error';
import { clearSiteStateAndReload } from '@/lib/recovery';

/**
 * Root-level error boundary — Next.js's last line of defence.
 *
 * `global-error.tsx` is the *only* error file that wraps the
 * `<html>` / `<body>` shell, so it's the surface that catches any
 * render-time throw escaping every other boundary in the tree. The
 * existing `(lobby)/error.tsx` only catches errors that happen
 * *inside* `<main>` for the lobby route group; anything thrown from
 * the AppShell chrome (`MobileChromeTop` / `MobileChromeBottom`,
 * including `MobileBoardRail`, `GlobalActivityTicker`, and
 * `AppBottomNav`), the music controller, or the providers
 * themselves used to fall through to Next.js's bare
 *   "Application error: a client-side exception has occurred"
 * fallback, with no recovery path and no useful console output for
 * support tickets — the exact failure mode reported on iPhone with
 * MetaMask after a ~few-second post-connect window (the portfolio
 * fetch landed and a malformed bigint field threw inside
 * `seat-utils.ts`, escaping `(lobby)/error.tsx` because the rail
 * lives in `AppShell.MobileChromeTop` which is rendered *above*
 * `<main>`).
 *
 * The render output here intentionally re-declares `<html>` and
 * `<body>` (Next.js requirement for `global-error.tsx`) and avoids
 * importing any layout-level provider — by the time we render we
 * cannot trust the provider tree did not throw. Tailwind classes
 * compile to static utility CSS so they're safe to use even with
 * the rest of the app torn down.
 *
 * The chrome mirrors `(lobby)/error.tsx` (same Retry / Go home
 * affordances, same `error.digest` surfacing) so the user
 * experience reads as the same recovery surface regardless of
 * which boundary caught the throw.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Structured log so the next reproduction lands every useful
    // piece of context next to the stack in DevTools — pathname
    // (which surface threw?), digest (matches the on-screen `ref`
    // so support tickets can be correlated against server logs),
    // userAgent (mobile webview vs desktop browser), and the raw
    // Error itself (Chrome / Safari expand the stack inline when
    // an Error object is logged). Production Next.js obfuscates
    // `error.message`, so the digest + pathname + UA triple is
    // what user-side consoles actually carry.
    const pathname =
      typeof window !== 'undefined' ? window.location.pathname : 'unknown';
    const userAgent =
      typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown';
    console.error('[global-error-boundary]', {
      pathname,
      userAgent,
      digest: error?.digest,
      name: error?.name,
      message: error?.message,
      error,
    });
    // Belt-and-braces: also POST to `/api/client-error` directly.
    // The `console.error` patch installed by `<ErrorTrap />` will
    // also pick up the line above and forward it (with the React
    // `componentStack` it captured a moment earlier on the same
    // tick) — the reporter de-dupes by `(source|name|message|stack-head)`
    // within a 5s window, so the two paths converge to a single
    // server log line. We still call directly here in case
    // `<ErrorTrap />` failed to mount (third-party script throwing
    // in the providers tree before our `useEffect` runs, etc.).
    reportClientError({
      source: 'global-error-boundary',
      error,
      digest: error?.digest ?? null,
    });
  }, [error]);

  const isDev = process.env.NODE_ENV !== 'production';
  // Decode `Minified React error #N` to the real human-readable
  // message. In production the boundary's only signal is currently
  // "Minified React error #185; visit https://react.dev/errors/185
  // …" — useless to a user, useless to anyone reading a screenshot.
  // The decode table lives next to the reporter (only a few codes,
  // refreshed by hand when new ones surface in the wild).
  const decoded = decodeReactError(error?.message);

  // "Reset session & reload" — same recovery affordance as
  // `(lobby)/error.tsx`. Surfaced here too because `global-error.tsx`
  // is the boundary that catches anything escaping the lobby tree
  // (provider crashes, AppShell chrome, music controller), which is
  // exactly when a stale Privy auth cookie is most likely to be the
  // culprit. See `src/lib/recovery.ts` for the wipe sequence.
  const [resetting, setResetting] = useState(false);
  const handleResetSession = async () => {
    if (resetting) return;
    setResetting(true);
    await clearSiteStateAndReload();
  };
  // Promote "Reset session" to the primary CTA when the captured
  // error matches the Next 14.x router-cache crash signature or the
  // React #185 escalation our fallback used to produce. The signature
  // is documented at length in `(lobby)/error.tsx`'s header.
  const isFrameworkCacheCrash =
    typeof error?.message === 'string' &&
    (error.message.includes("reading 'get'") ||
      error.message.includes('Minified React error #185') ||
      error.message.includes('Maximum update depth exceeded'));
  const replicaShort =
    typeof document !== 'undefined'
      ? document
          .querySelector('meta[name="x-replica-short"]')
          ?.getAttribute('content') ?? 'unknown'
      : 'unknown';
  const replicaRegion =
    typeof document !== 'undefined'
      ? document
          .querySelector('meta[name="x-replica-region"]')
          ?.getAttribute('content') ?? 'unknown'
      : 'unknown';

  return (
    <html lang="en" className="dark">
      <body
        style={{
          margin: 0,
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#07050a',
          color: '#e4e4e7',
          fontFamily:
            'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
          padding: '24px',
        }}
      >
        {/* Inline styles only — Tailwind layer ordering is unreliable
            in the global-error tree because the layout's `globals.css`
            import may itself be the throw site. Inline keeps the
            recovery surface readable on a worst-case mount. */}
        <div style={{ maxWidth: 384, width: '100%', textAlign: 'center' }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 48,
              height: 48,
              borderRadius: '9999px',
              border: '1px solid rgba(245,158,11,0.4)',
              backgroundColor: 'rgba(245,158,11,0.1)',
              color: '#fcd34d',
              marginBottom: 16,
            }}
          >
            <span aria-hidden style={{ fontFamily: 'ui-monospace, monospace', fontSize: 14 }}>
              !
            </span>
          </div>
          <h1
            style={{
              fontSize: 18,
              fontWeight: 600,
              color: '#fafafa',
              margin: '0 0 8px',
            }}
          >
            Something went wrong.
          </h1>
          <p style={{ fontSize: 14, color: '#a1a1aa', margin: '0 0 16px' }}>
            {isFrameworkCacheCrash
              ? 'A stale session is wedging the page. Reset to clear it.'
              : 'Tap retry to reload, or head back to the homepage.'}
          </p>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              paddingTop: 4,
              flexWrap: 'wrap',
            }}
          >
            {/*
              `reset()` is Next.js's app-router boundary recovery —
              re-renders the failing segment without remounting the
              tree. Safe here (this is global-error, not the lobby
              fallback that suffered the `<Link>` prefetch loop), so
              we keep it as the lightest-weight first attempt.
            */}
            <button
              type="button"
              onClick={reset}
              style={{
                padding: '6px 14px',
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 500,
                color: '#fafafa',
                backgroundColor: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.12)',
                cursor: 'pointer',
              }}
            >
              Retry
            </button>
            <a
              href="/"
              style={{
                padding: '6px 14px',
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 500,
                color: '#a1a1aa',
                textDecoration: 'none',
                border: '1px solid transparent',
              }}
            >
              Go home
            </a>
            {/* Escape hatch into the rescue surface. `/debug`
                short-circuits `<Providers>` and `<AppShell>` (see
                `src/providers/index.tsx` and
                `src/components/layout/app-shell.tsx`) so it paints
                even when the rest of the app is wedged. Plain
                `<a href>` by design: the framework-cache crash this
                boundary most often catches also corrupts the Next
                14.x router cache, so any `<Link>` / `router.push`
                would walk the user straight back into the same
                throw — see `(lobby)/error.tsx`'s header for the
                full chain. A full-document navigation dodges it. */}
            <a
              href="/debug"
              style={{
                padding: '6px 14px',
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 500,
                color: '#a1a1aa',
                textDecoration: 'none',
                border: '1px solid transparent',
              }}
            >
              Open diagnostics
            </a>
            {/*
              Reset-session affordance. Promoted to amber primary
              when the error signature matches the framework-cache
              crash pattern (`reading 'get'` or React #185); a
              quieter outline button otherwise. Same destructive
              `clearSiteStateAndReload` underneath in both shapes.
            */}
            <button
              type="button"
              onClick={handleResetSession}
              disabled={resetting}
              style={
                isFrameworkCacheCrash
                  ? {
                      padding: '6px 14px',
                      borderRadius: 6,
                      fontSize: 13,
                      fontWeight: 600,
                      color: '#09090b',
                      backgroundColor: 'rgba(245,158,11,0.95)',
                      border: '1px solid rgba(245,158,11,0.95)',
                      cursor: resetting ? 'progress' : 'pointer',
                      opacity: resetting ? 0.6 : 1,
                    }
                  : {
                      padding: '6px 14px',
                      borderRadius: 6,
                      fontSize: 13,
                      fontWeight: 500,
                      color: '#d4d4d8',
                      backgroundColor: 'transparent',
                      border: '1px solid rgba(255,255,255,0.18)',
                      cursor: resetting ? 'progress' : 'pointer',
                      opacity: resetting ? 0.6 : 1,
                    }
              }
            >
              {resetting ? 'Resetting…' : 'Reset session & reload'}
            </button>
          </div>
          <p
            style={{
              fontSize: 11,
              color: '#71717a',
              margin: '8px 0 0',
            }}
          >
            Reset signs you out and clears your saved drafts and
            preferences for this site.
          </p>
          {error?.digest || replicaShort !== 'unknown' ? (
            <div
              style={{
                paddingTop: 8,
                fontFamily: 'ui-monospace, monospace',
                fontSize: 10,
                color: '#52525b',
                wordBreak: 'break-all',
              }}
            >
              {error?.digest ? <>ref {error.digest}</> : null}
              {error?.digest && replicaShort !== 'unknown' ? ' · ' : null}
              {/* Replica id + region pulled from the SSR meta tags so
                  a user sharing a screenshot of this card hands us
                  enough information to filter Railway logs to the
                  one process that produced the broken render. The
                  full UUID lives in `meta[name="x-replica-id"]`;
                  what's printed here is the 8-char short form. */}
              {replicaShort !== 'unknown' ? (
                <>
                  replica {replicaShort}
                  {replicaRegion !== 'unknown' ? ` · ${replicaRegion}` : ''}
                </>
              ) : null}
            </div>
          ) : null}
          {/*
            Decoded error block — surfaced in production too.
            Previously gated on `isDev` because `error.message` was
            "Minified React error #185" with no actionable info, but
            the decode table now expands the React invariant codes
            into human-readable copy (`Maximum update depth
            exceeded…`) so a user-reported screenshot already
            includes the diagnosis. The full minified-stack URL is
            kept for cross-referencing with `react.dev/errors/N`.
            Falls back to `error.message` when no React code matches.
           */}
          {decoded.code ? (
            <pre
              style={{
                marginTop: 8,
                maxHeight: 200,
                overflow: 'auto',
                borderRadius: 6,
                border: '1px solid rgba(245,158,11,0.25)',
                backgroundColor: 'rgba(0,0,0,0.6)',
                padding: 8,
                textAlign: 'left',
                fontFamily: 'ui-monospace, monospace',
                fontSize: 10,
                lineHeight: 1.4,
                color: 'rgba(252,211,77,0.9)',
                whiteSpace: 'pre-wrap',
              }}
            >
              {`React #${decoded.code}${decoded.decoded ? `: ${decoded.decoded}` : ''}`}
              {error?.message ? `\n\n${error.message}` : ''}
            </pre>
          ) : isDev && error?.message ? (
            <pre
              style={{
                marginTop: 8,
                maxHeight: 160,
                overflow: 'auto',
                borderRadius: 6,
                border: '1px solid rgba(245,158,11,0.15)',
                backgroundColor: 'rgba(0,0,0,0.5)',
                padding: 8,
                textAlign: 'left',
                fontFamily: 'ui-monospace, monospace',
                fontSize: 10,
                lineHeight: 1.4,
                color: 'rgba(252,211,77,0.8)',
                whiteSpace: 'pre-wrap',
              }}
            >
              {error.message}
            </pre>
          ) : null}
        </div>
      </body>
    </html>
  );
}
